import React, { useState, useEffect, useContext, memo } from 'react';
import { TabsContent, } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { apiFetch } from "../../utils/api";
import { useAuth } from '../../Services/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

import {
  Plus,
  Search,
  Eye,
  Edit,
  Users,
  Building2,
  Mail,
  Phone,
  MapPin,
  CheckCircle,
  Trash2,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';

import {
  fetchSegments,
  fetchCountriesLookup
} from '../../Services/SalesRenewalsService';

export const EndUserAccountsTab = memo(({ }) => {

  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [localEndUsers, setLocalEndUsers] = useState([]);
  const [selectedEndUser, setSelectedEndUser] = useState(null);
  const [isViewEndUserOpen, setIsViewEndUserOpen] = useState(false);
  const [isEditEndUserOpen, setIsEditEndUserOpen] = useState(false);
  const [editEndUser, setEditEndUser] = useState(null);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [isCreateEndUserOpen, setIsCreateEndUserOpen] = useState(false);
  const [segments, setSegments] = useState([]);
  const [countries, setCountries] = useState([]);

  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  const [newEndUser, setNewEndUser] = useState({
    name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    industry: '',
    status: 'Active',
  });

  // Edit Contact dialog state
  const [isEditContactOpen, setIsEditContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editContactData, setEditContactData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
  });

  const {
    idToken,
    currentPartner,
  } = useAuth();

  const fetchEndUsers = async () => {
    let tk = await idToken();
    const res = apiFetch(tk, `/api/partners/end_users?partner_id=${currentPartner.partner_id}&search_term=${customerSearchTerm}&company_id=${currentPartner.company_id}`);
    res.then(async (response) => {
      setLocalEndUsers(response.data);
    });
  };

  // Handle edit contact
  const handleEditContactClick = (contact, endUserId) => {
    setEditingContact({ ...contact, endUserId });
    setEditContactData({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      phone_number: contact.phone_number,
    });
    setIsEditContactOpen(true);
  };

  // Handle save edited contact
  const handleSaveEditedContact = async () => {
    // Validate required fields
    if (!editContactData.first_name || !editContactData.last_name || !editContactData.email) {
      alert('Please fill in all required fields (Name and Email)');
      return;
    }

    const endUserId = editingContact.endUserId;
    const contactId = editingContact.id;

    // Find the end user
    const endUserIndex = localEndUsers.findIndex(user => user.id === endUserId);
    if (endUserIndex === -1) return;

    // Update contact in the end user's contacts array
    const updatedEndUsers = [...localEndUsers];
    const contactIndex = updatedEndUsers[endUserIndex].contacts.findIndex((c) => c.id === contactId);
    
    if (contactIndex !== -1) {
      updatedEndUsers[endUserIndex].contacts[contactIndex] = {
        ...updatedEndUsers[endUserIndex].contacts[contactIndex],
        first_name: editContactData.first_name,
        last_name: editContactData.last_name,
        email: editContactData.email,
        phone_number: editContactData.phone_number, 
      };
    }

    setLocalEndUsers(updatedEndUsers);

    // Update selectedEndUser if it's the same user
    if (selectedEndUser?.id === endUserId) {
      const updatedContacts = [...selectedEndUser.contacts];
      const idx = updatedContacts.findIndex((c) => c.id === contactId);
      if (idx !== -1) {
        updatedContacts[idx] = {
          ...updatedContacts[idx],
          first_name: editContactData.first_name,
          last_name: editContactData.last_name,
          email: editContactData.email,
          phone_number: editContactData.phone_number,
        };
      }
      setSelectedEndUser({
        ...selectedEndUser,
        contacts: updatedContacts
      });
    }

    // Update editEndUser if it's the same user
    if (editEndUser?.id === endUserId) {
      const updatedContacts = [...editEndUser.contacts];
      const idx = updatedContacts.findIndex((c) => c.id === contactId);
      if (idx !== -1) {
        updatedContacts[idx] = {
          ...updatedContacts[idx],
          first_name: editContactData.first_name,
          last_name: editContactData.last_name,
          email: editContactData.email,
          phone_number: editContactData.phone_number,
        };
      }
      setEditEndUser({
        ...editEndUser,
        contacts: updatedContacts
      });
    }

    const res = await apiFetch(await idToken(), `/api/contacts/${contactId}?partner_id=${currentPartner.partner_id}`, {
      method: "PATCH",
      body: JSON.stringify(editContactData)
    });


    // Reset and close
    setIsEditContactOpen(false);
    setEditingContact(null);
    // toast.success('Contact updated successfully!');
  };

  const handleAddContact = async (endUserId) => {
    // Validate required fields
    if (!newContact.first_name || !newContact.last_name || !newContact.email) {
      alert('Please fill in all required fields (Name and Email)');
      return;
    }

    // Find the end user
    const endUserIndex = localEndUsers.findIndex(user => user.id === endUserId);
    if (endUserIndex === -1) return;

    // Create new contact
    const contactId = `contact-${endUserId}-${Date.now()}`;
    const contactToAdd = {
      id: contactId,
      first_name: newContact.first_name,
      last_name: newContact.last_name,
      email: newContact.email,
      phone_number: newContact.phone_number,
      end_user_id: endUserId
    };

    // Update end user with new contact
    const updatedEndUsers = [...localEndUsers];
    updatedEndUsers[endUserIndex] = {
      ...updatedEndUsers[endUserIndex],
      contacts: [...(updatedEndUsers[endUserIndex].contacts || []), contactToAdd]
    };

    setLocalEndUsers(updatedEndUsers);

    // If this is being added to the selected/editing end user, update those states too
    if (selectedEndUser?.id === endUserId) {
      setSelectedEndUser({
        ...selectedEndUser,
        contacts: [...(selectedEndUser.contacts || []), contactToAdd]
      });
    }

    if (editEndUser?.id === endUserId) {
      setEditEndUser({
        ...editEndUser,
        contacts: [...(editEndUser.contacts || []), contactToAdd]
      });
    }

    // Reset form and close dialog
    setNewContact({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role: '',
      isPrimary: false
    });
    setIsAddContactOpen(false);

    const res = await apiFetch(await idToken(), `/api/contacts?partner_id=${currentPartner.partner_id}`, {
      method: "POST",
      body: JSON.stringify(contactToAdd)
    });

  };

  const handleCreateEndUser = async () => {
    // Validate required fields
    if (!newEndUser.name || !newEndUser.first_name || !newEndUser.email) {
      alert('Please fill in all required fields (Company Name, Contact Person, Email)');
      return;
    }

    // Validate address fields
    if (!newEndUser.address_street || !newEndUser.address_city || !newEndUser.address_zip_code || !newEndUser.address_country_region_id) {
      alert('Please fill in all required address fields (Street Address, City, Postal Code, Country)');
      return;
    }

    // Generate ID
    const newId = `eu${String(localEndUsers.length + 1).padStart(3, '0')}`;

    // Create the new end user object with primary contact
    const createdEndUser = {
      id: newId,
      end_user_id: newId,
      name: newEndUser.name,
      email: newEndUser.email,
      segment_id: newEndUser.segment_id,
      first_name: newEndUser.first_name,
      last_name: newEndUser.last_name,
      phone: newEndUser.phone || '+27 (11) 000-0000',
      address: newEndUser.address,
      address_city: newEndUser.address_city,
      address_street: newEndUser.address_street,
      address_zip_code: newEndUser.address_zip_code,
      address_country_region_id: newEndUser.address_country_region_id,
      industry: newEndUser.industry,
      licenses: newEndUser.licenses,
      status: newEndUser.status,
      csn: newEndUser.csn,
      contacts: [
        {
          id: `contact-${newId}-1`,
          first_name: newEndUser.first_name,
          last_name: newEndUser.last_name,
          email: newEndUser.email,
          phone: newEndUser.phone || '+27 (11) 000-0000',
          role: 'Primary Contact',
          isPrimary: true
        }
      ]
    };

    const res = await apiFetch(await idToken(), `/api/end_users?partner_id=${currentPartner.partner_id}&company_id=${currentPartner.company_id}`, {
      method: "POST",
      body: JSON.stringify(createdEndUser)
    });
    createdEndUser.id = res.data.id;

    // Add to local end users list
    setLocalEndUsers([...localEndUsers, createdEndUser]);

    // Reset form
    setNewEndUser({
      name: '',
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      address_street: '',
      address_city: '',
      address_zip_code: '',
      address_country_region_id: 'ZAF',
      industry: '',
      licenses: 0,
      status: 'Active',
      csn: ''
    });

    // Close dialog
    setIsCreateEndUserOpen(false);
  };

  useEffect(() => {
    fetchEndUsers();
    fetchSegments(idToken, setSegments);
    fetchCountriesLookup(idToken, setCountries);
  }, [])

  useEffect(() => {
    fetchEndUsers();
  }, [customerSearchTerm])

  return (
    <>
    {/* Edit Contact Dialog */}
    <Dialog open={isEditContactOpen} onOpenChange={setIsEditContactOpen}>
        <DialogContent className="glass-modal max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-worldsview-blue" />
              Edit Contact
            </DialogTitle>
            <DialogDescription>
              Update the contact information below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-contact-name-first">
                  Contact First Name <span className="text-worldsview-red">*</span>
                </Label>
                <Input
                  id="edit-contact-name-first"
                  placeholder="First Name"
                  value={editContactData.first_name}
                  onChange={(e) => setEditContactData({ ...editContactData, first_name: e.target.value })}
                  className="glass-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-contact-name-last">
                  Contact Last Name <span className="text-worldsview-red">*</span>
                </Label>
                <Input
                  id="edit-contact-name-last"
                  placeholder="Last Name"
                  value={editContactData.last_name}
                  onChange={(e) => setEditContactData({ ...editContactData, last_name: e.target.value })}
                  className="glass-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-contact-email">
                  Email <span className="text-worldsview-red">*</span>
                </Label>
                <Input
                  id="edit-contact-email"
                  type="email"
                  placeholder="email@example.com"
                  value={editContactData.email}
                  onChange={(e) => setEditContactData({ ...editContactData, email: e.target.value })}
                  className="glass-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-contact-phone">Phone</Label>
                <Input
                  id="edit-contact-phone"
                  placeholder="+27 (11) 000-0000"
                  value={editContactData.phone_number}
                  onChange={(e) => setEditContactData({ ...editContactData, phone_number: e.target.value })}
                  className="glass-input"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setIsEditContactOpen(false);
                setEditingContact(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-worldsview-blue hover:bg-worldsview-blue/90"
              onClick={handleSaveEditedContact}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create End User Dialog (from End User Accounts Tab) */}
      <Dialog open={isCreateEndUserOpen} onOpenChange={setIsCreateEndUserOpen}>
        <DialogContent className="glass-modal max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-worldsview-blue" />
              Create New End User Account
            </DialogTitle>
            <DialogDescription>
              Add a new end user account to your customer database.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Company Information */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Building2 className="w-4 h-4 text-worldsview-blue" />
                Company Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="company-name-main">
                    Company Name <span className="text-worldsview-red">*</span>
                  </Label>
                  <Input
                    id="company-name-main"
                    placeholder="Enter company name"
                    value={newEndUser.name}
                    onChange={(e) => setNewEndUser({ ...newEndUser, name: e.target.value })}
                    className="glass-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry-main">Industry</Label>
                  <Select value={newEndUser.segment_id} onValueChange={(value) => setNewEndUser({ ...newEndUser, segment_id: value })}>
                    <SelectTrigger id="industry-main" className="glass-input">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {segments.map((segment) => (
                        <SelectItem key={segment} value={segment}>
                          {segment}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

              </div>
            </div>

            {/* Primary Contact Information */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <Users className="w-4 h-4 text-worldsview-blue" />
                Primary Contact Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-person-main">
                    First Name <span className="text-worldsview-red">*</span>
                  </Label>
                  <Input
                    id="contact-person-main-fname"
                    placeholder="First name"
                    value={newEndUser.first_name}
                    onChange={(e) => setNewEndUser({ ...newEndUser, first_name: e.target.value })}
                    className="glass-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-person-main">
                    Last Name <span className="text-worldsview-red">*</span>
                  </Label>
                  <Input
                    id="contact-person-main"
                    placeholder="Full name"
                    value={newEndUser.last_name}
                    onChange={(e) => setNewEndUser({ ...newEndUser, last_name: e.target.value })}
                    className="glass-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email-main">
                    Email <span className="text-worldsview-red">*</span>
                  </Label>
                  <Input
                    id="email-main"
                    type="email"
                    placeholder="email@company.com"
                    value={newEndUser.email}
                    onChange={(e) => setNewEndUser({ ...newEndUser, email: e.target.value })}
                    className="glass-input"
                  />
                </div>

                <div className="space-y-2 col-span-2">
                  <Label htmlFor="phone-main">Phone</Label>
                  <Input
                    id="phone-main"
                    placeholder="+27 (11) 000-0000"
                    value={newEndUser.phone}
                    onChange={(e) => setNewEndUser({ ...newEndUser, phone: e.target.value })}
                    className="glass-input"
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4 text-worldsview-blue" />
                Address Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="address-main">
                    Street Address <span className="text-worldsview-red">*</span>
                  </Label>
                  <Input
                    id="address-main"
                    placeholder="123 Main Street"
                    value={newEndUser.address_street}
                    onChange={(e) => setNewEndUser({ ...newEndUser, address_street: e.target.value })}
                    className="glass-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city-main">
                    City <span className="text-worldsview-red">*</span>
                  </Label>
                  <Input
                    id="city-main"
                    placeholder="City"
                    value={newEndUser.address_city}
                    onChange={(e) => setNewEndUser({ ...newEndUser, address_city: e.target.value })}
                    className="glass-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postal-code-main">
                    Postal Code <span className="text-worldsview-red">*</span>
                  </Label>
                  <Input
                    id="postal-code-main"
                    placeholder="0000"
                    value={newEndUser.address_zip_code}
                    onChange={(e) => setNewEndUser({ ...newEndUser, address_zip_code: e.target.value })}
                    className="glass-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country-main">
                    Country <span className="text-worldsview-red">*</span>
                  </Label>
                  <Select 
                    value={newEndUser.address_country_region_id} 
                    onValueChange={(value) => setNewEndUser({ ...newEndUser, address_country_region_id: value })}
                    required
                  >
                    <SelectTrigger id="country-main" className="glass-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.value} value={country.value}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Status */}
            {/* <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status-main">Status</Label>
                  <Select value={newEndUser.status} onValueChange={(value) => setNewEndUser({ ...newEndUser, status: value })}>
                    <SelectTrigger id="status-main" className="glass-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Pending Renewal">Pending Renewal</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div> */}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateEndUserOpen(false);
                setNewEndUser({
                  name: '',
                  contactPerson: '',
                  email: '',
                  phone: '',
                  address_street: '',
                  address_city: '',
                  address_zip_code: '',
                  address_country_region_id: 'ZAF',
                  industry: '',
                  licenses: 0,
                  status: 'Active',
                  csn: ''
                });
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-worldsview-blue hover:bg-worldsview-blue/90"
              onClick={handleCreateEndUser}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create End User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={isAddContactOpen} onOpenChange={setIsAddContactOpen}>
        <DialogContent className="glass-modal max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-worldsview-green" />
              Add New Contact
            </DialogTitle>
            <DialogDescription>
              Add a new contact to this end user account.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact-name">
                  First Name <span className="text-worldsview-red">*</span>
                </Label>
                <Input
                  id="contact-name"
                  placeholder="First Name"
                  value={newContact.first_name}
                  onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                  className="glass-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-name">
                  Last Name <span className="text-worldsview-red">*</span>
                </Label>
                <Input
                  id="contact-name"
                  placeholder="Last Name"
                  value={newContact.last_name}
                  onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                  className="glass-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-email">
                  Email <span className="text-worldsview-red">*</span>
                </Label>
                <Input
                  id="contact-email"
                  type="email"
                  placeholder="email@company.com"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  className="glass-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-phone">Phone</Label>
                <Input
                  id="contact-phone"
                  type="tel"
                  placeholder="+27 (11) 000-0000"
                  value={newContact.phone_number}
                  onChange={(e) => setNewContact({ ...newContact, phone_number: e.target.value })}
                  className="glass-input"
                />
              </div>

            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setIsAddContactOpen(false);
                setNewContact({
                  name: '',
                  email: '',
                  phone: '',
                  role: '',
                  isPrimary: false
                });
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-worldsview-green hover:bg-worldsview-green/90"
              onClick={() => {
                const endUserId = selectedEndUser?.id || editEndUser?.id;
                if (endUserId) {
                  handleAddContact(endUserId);
                }
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>



      {/* Edit End User Dialog */}
      <Dialog open={isEditEndUserOpen} onOpenChange={setIsEditEndUserOpen}>
        <DialogContent className="glass-modal max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-worldsview-purple" />
              Edit End User
            </DialogTitle>
            <DialogDescription>
              Update end user details and manage contacts.
            </DialogDescription>
          </DialogHeader>

          {editEndUser && (
            <div className="space-y-6">
              {/* Company Information */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-worldsview-blue" />
                  Company Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="edit-company-name">
                      Company Name <span className="text-worldsview-red">*</span>
                    </Label>
                    <Input
                      id="edit-company-name"
                      value={editEndUser.name}
                      onChange={(e) => setEditEndUser({ ...editEndUser, name: e.target.value })}
                      className="glass-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-csn">
                      CSN
                    </Label>
                    <Input
                      id="edit-csn"
                      value={editEndUser.csn}
                      onChange={(e) => setEditEndUser({ ...editEndUser, csn: e.target.value })}
                      className="glass-input"
                      disabled
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit-industry">Industry</Label>
                    <Select value={editEndUser.segment_id || ''} onValueChange={(value) => setEditEndUser({ ...editEndUser, segment_id: value })}>
                      <SelectTrigger id="edit-industry" className="glass-input">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {segments.map((segment) => (
                          <SelectItem key={segment} value={segment}>
                            {segment}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-worldsview-blue" />
                  Address Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="address-main">
                        Street Address <span className="text-worldsview-red">*</span>
                      </Label>
                      <Input
                        id="address-main"
                        placeholder="123 Main Street"
                        value={editEndUser.address_street}
                        onChange={(e) => setEditEndUser({ ...editEndUser, address_street: e.target.value })}
                        className="glass-input"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="city-main">
                        City <span className="text-worldsview-red">*</span>
                      </Label>
                      <Input
                        id="city-main"
                        placeholder="City"
                        value={editEndUser.address_city}
                        onChange={(e) => setEditEndUser({ ...editEndUser, address_city: e.target.value })}
                        className="glass-input"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="postal-code-main">
                        Postal Code <span className="text-worldsview-red">*</span>
                      </Label>
                      <Input
                        id="postal-code-main"
                        placeholder="0000"
                        value={editEndUser.address_zip_code}
                        onChange={(e) => setEditEndUser({ ...editEndUser, address_zip_code: e.target.value })}
                        className="glass-input"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="country-main">
                        Country <span className="text-worldsview-red">*</span>
                      </Label>
                      <Select 
                        value={editEndUser.address_country_region_id} 
                        onValueChange={(value) => setEditEndUser({ ...editEndUser, address_country_region_id: value })}
                        required
                      >
                        <SelectTrigger id="country-main" className="glass-input">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country.value} value={country.value}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contacts */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium flex items-center gap-2">
                    <Users className="w-4 h-4 text-worldsview-blue" />
                    Contacts ({editEndUser.contacts?.length || 0})
                  </h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsAddContactOpen(true)}
                    className="border-worldsview-blue text-worldsview-blue hover:bg-worldsview-blue/10"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Contact
                  </Button>
                </div>
                <div className="space-y-3">
                  {editEndUser.contacts && editEndUser.contacts.length > 0 ? (
                    editEndUser.contacts.map((contact) => (
                      <Card key={contact.id} className="glass-card">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Mail className="w-4 h-4" />
                                <span>{contact.email}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Phone className="w-4 h-4" />
                                <span>{contact.phone_number}</span>
                              </div>
                              
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEditContactClick(contact, editEndUser.id)}
                                  className="h-8 w-8 p-0 text-worldsview-blue hover:bg-worldsview-blue/10"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground p-4 text-center bg-muted/20 rounded-lg">
                      No contacts added yet. Click "Add Contact" to add a new contact.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t mt-6">
            <Button
              variant="outline"
              onClick={() => setIsEditEndUserOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-worldsview-purple hover:bg-worldsview-purple/90"
              onClick={async () => {
                // Validate address fields
                if (!editEndUser.address_street || !editEndUser.address_city || !editEndUser.address_zip_code || !editEndUser.address_country_region_id) {
                  alert('Please fill in all required address fields (Street Address, City, Postal Code, Country)');
                  return;
                }

                // Update the end user in the list
                const updatedEndUsers = localEndUsers.map(user =>
                  user.id === editEndUser.id ? editEndUser : user
                );
                setLocalEndUsers(updatedEndUsers);
                setIsEditEndUserOpen(false);

                const res = await apiFetch(await idToken(), `/api/end_users?partner_id=${currentPartner.partner_id}&company_id=${currentPartner.company_id}`, {
                  method: "PATCH",
                  body: JSON.stringify(editEndUser)
                });
              }}
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View End User Dialog */}
      <Dialog open={isViewEndUserOpen} onOpenChange={setIsViewEndUserOpen}>
        <DialogContent className="glass-modal max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-worldsview-blue" />
              End User Details
            </DialogTitle>
            <DialogDescription>
              View complete details and contact information for this end user account.
            </DialogDescription>
          </DialogHeader>

          {selectedEndUser && (
            <div className="space-y-6">
              {/* Company Information */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-worldsview-blue" />
                  Company Information
                </h3>
                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <Label className="text-sm text-muted-foreground">Company Name</Label>
                    <p className="font-medium">{selectedEndUser.name}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Account ID</Label>
                    <p className="font-medium">{selectedEndUser.end_user_id?.toUpperCase()}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">CSN</Label>
                    <p className="font-medium">{selectedEndUser.csn}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Industry</Label>
                    <p className="font-medium">{selectedEndUser.segment_id || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-worldsview-blue" />
                  Address Information
                </h3>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <div className="space-y-2">
                    {selectedEndUser.address_street && <p>{selectedEndUser.address_street}</p>}
                    {selectedEndUser.address_city && <p>{selectedEndUser.address_city}</p>}
                    {selectedEndUser.address_zip_code && <p>{selectedEndUser.address_zip_code}</p>}
                    {selectedEndUser.address_country_region_id && <p>{selectedEndUser.address_country_region_id}</p>}
                  </div>
                </div>
              </div>

              {/* Contacts */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium flex items-center gap-2">
                    <Users className="w-4 h-4 text-worldsview-blue" />
                    Contacts ({selectedEndUser.contacts?.length || 0})
                  </h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsAddContactOpen(true)}
                    className="border-worldsview-blue text-worldsview-blue hover:bg-worldsview-blue/10"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Contact
                  </Button>
                </div>
                <div className="space-y-3">
                  {selectedEndUser.contacts && selectedEndUser.contacts.length > 0 ? (
                    selectedEndUser.contacts.map((contact) => (
                      <Card key={contact.id} className="glass-card">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Mail className="w-4 h-4" />
                                <span>{contact.email}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Phone className="w-4 h-4" />
                                <span>{contact.phone_number}</span>
                              </div>
                              {contact.role && (
                                <div className="text-sm">
                                  <span className="text-muted-foreground">Role: </span>
                                  <span className="font-medium">{contact.role}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditContactClick(contact, selectedEndUser.id)}
                                className="h-8 w-8 p-0 text-worldsview-blue hover:bg-worldsview-blue/10"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground p-4 text-center bg-muted/20 rounded-lg">
                      No contacts added yet. Click "Add Contact" to add a new contact.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t mt-6">
            <Button
              variant="outline"
              onClick={() => setIsViewEndUserOpen(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <TabsContent value="end-users" className="space-y-6">
        <Card className="glass-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-worldsview-blue" />
                End User Accounts
              </CardTitle>
              <Button
                className="bg-worldsview-blue hover:bg-worldsview-blue/90"
                onClick={() => setIsCreateEndUserOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                New End User
              </Button>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Search and Filters */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search end users..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  className="pl-10 glass-input"
                />
              </div>
            </div>

            {/* End Users Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {localEndUsers.map((endUser) => (
                <Card key={endUser.end_user_id} className="glass-card hover:scale-[1.02] transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-worldsview-blue/20 rounded-full flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-worldsview-blue" />
                        </div>
                        <div>
                          <h3 className="font-medium">{endUser.name}</h3>
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-muted-foreground">{endUser.end_user_id?.toUpperCase()}</p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(endUser.end_user_id?.toUpperCase());
                                toast.success('ID copied to clipboard!');
                              }}
                              className="text-muted-foreground hover:text-worldsview-blue transition-colors"
                              title="Copy ID to clipboard"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <p className="text-xs text-muted-foreground">CSN: {endUser.csn || '-'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span>{endUser.contacts[0]?.phone_number}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span>{endUser.contacts[0]?.email}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="flex flex-col">
                          <span className="text-sm">{endUser.address_street}</span>
                          <span className="text-sm">{endUser.address_city}</span>
                          <span className="text-sm">{endUser.address_zip_code}</span>
                          <span className="text-sm">{endUser.address_country_region_id}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-4 pt-3 border-t">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedEndUser(endUser);
                            setIsViewEndUserOpen(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditEndUser(endUser);
                            setIsEditEndUserOpen(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </TabsContent>

    </>
  )

});

